// apps/client/src/pages/pipeline-tracker/tabs/taskDrawerFilters.ts
//
// The filters behind the Operations > Tasks drawer's figure chips. Each one MIRRORS the rule the
// server counts its chip with (tasksReport.ts, buildFigures), so a filtered list holds exactly the
// tasks its chip counted:
//
//   Outstanding   to do or doing.
//   Overdue       outstanding, due strictly before the as-at date (a task due today is not missed).
//   Overdue, high priority  overdue, and the task's normalised priority is High. A task with no
//                 priority is "not set" and never counts here.
//   Due in window due on or between the window's first and last day, whatever its state.
//   Done in window done, among the tasks due in the window.
//
// Completed % is a ratio of two of these, not a set of tasks, so it is not a filter.

import type { TaskDrilldownRow, TaskPriority } from '../../../hooks/pipelineTracker/useTasksReport';

export type TaskDrawerFilter = 'all' | 'outstanding' | 'overdue' | 'overdueHigh' | 'dueInWindow' | 'doneInWindow';

/** How each normalised priority reads on screen. "Not set", never "Low", for a blank. */
export const TASK_PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  not_set: 'Not set',
};

export interface TaskDrawerScope {
  /** The snapshot's as-at date, 'YYYY-MM-DD'. */
  asAtDate: string;
  window: { start: string; end: string };
}

const isOpen = (row: TaskDrilldownRow): boolean => row.state === 'todo' || row.state === 'doing';

/** Mirrors the server's isOverdue: open, due strictly before the as-at date. */
const isOverdue = (row: TaskDrilldownRow, scope: TaskDrawerScope): boolean =>
  isOpen(row) && row.dueDate !== null && row.dueDate < scope.asAtDate;

const inWindow = (row: TaskDrilldownRow, scope: TaskDrawerScope): boolean =>
  row.dueDate !== null && row.dueDate >= scope.window.start && row.dueDate <= scope.window.end;

export function matchesTaskFilter(row: TaskDrilldownRow, filter: TaskDrawerFilter, scope: TaskDrawerScope): boolean {
  switch (filter) {
    case 'outstanding':
      return isOpen(row);
    case 'overdue':
      return isOverdue(row, scope);
    case 'overdueHigh':
      return isOverdue(row, scope) && row.priority === 'high';
    case 'dueInWindow':
      return inWindow(row, scope);
    case 'doneInWindow':
      return row.state === 'done' && inWindow(row, scope);
    default:
      return true;
  }
}

/** The most actionable view: overdue when anything is, else outstanding, else everything. */
export function defaultTaskFilter(rows: readonly TaskDrilldownRow[], scope: TaskDrawerScope): TaskDrawerFilter {
  if (rows.some((r) => matchesTaskFilter(r, 'overdue', scope))) return 'overdue';
  if (rows.some(isOpen)) return 'outstanding';
  return 'all';
}

/** Open first, then not classified, then done: work still to do leads every view. */
const STATE_RANK: Record<TaskDrilldownRow['state'], number> = { todo: 0, doing: 0, unclassified: 1, done: 2 };

/**
 * How filterTaskRows orders each filter's list, as the drawer says it. One sort serves every
 * filter (state band, then oldest due date first), so the words differ only where the list does:
 * a list of open work reads "most overdue first", a list of done work has nothing overdue in it,
 * and a mixed list puts its open work first.
 */
export function taskSortLabel(filter: TaskDrawerFilter): string {
  switch (filter) {
    case 'outstanding':
    case 'overdue':
    case 'overdueHigh':
      return 'most overdue first';
    case 'doneInWindow':
      return 'earliest due date first';
    default:
      return 'open first, then earliest due date';
  }
}

/**
 * The filtered list, most overdue (oldest due date) first within each state band, undated last.
 * The server sends oldest-due first across every state, which put two-year-old done tasks at the
 * top of the list; the state band keeps the open work ahead of them.
 */
export function filterTaskRows(
  rows: readonly TaskDrilldownRow[],
  filter: TaskDrawerFilter,
  scope: TaskDrawerScope,
): TaskDrilldownRow[] {
  return rows
    .filter((r) => matchesTaskFilter(r, filter, scope))
    .sort((a, b) => {
      const band = STATE_RANK[a.state] - STATE_RANK[b.state];
      if (band !== 0) return band;
      if (a.ageDays === null || b.ageDays === null) {
        if (a.ageDays === b.ageDays) return a.taskId.localeCompare(b.taskId);
        return a.ageDays === null ? 1 : -1;
      }
      return b.ageDays - a.ageDays || a.taskId.localeCompare(b.taskId);
    });
}
